import type { RouteLocationNormalizedLoaded, RouteRecordRaw } from 'vue-router'
import {
  guardCreateTraining,
  guardElearningCourseWorkspace,
  guardElearningAssignmentPlayer,
  guardOrganizeSession,
  guardTrainingAccess,
  guardTrainingBudget,
  guardTrainingEdit,
  guardTrainingStats,
  guardTrainingSettingsAccess,
  guardTrainingEvaluationFormsSettingsAccess,
  guardTrainingLibrary,
  guardTrainingWishesAttribution,
} from '../navigationsGuards/guardTraining'

const MODULE_NAME = 'TRAINING'
const MODULE_ID = 9
const ElearningCourseEditorPage = () => import('@/views/training/elearning/ElearningCourseEditorPage.vue')

const trainingRoutes: Array<RouteRecordRaw> = [
  {
    path: '/training',
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'TrainingList',
        component: () => import('@/views/training/TrainingList.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
        beforeEnter: [guardTrainingAccess, guardTrainingLibrary],
      },
      {
        path: 'new',
        name: 'TrainingNew',
        component: () => import('@/views/training/trainingNew/TrainingNew.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
        beforeEnter: [guardCreateTraining],
      },
      {
        path: 'my-trainings',
        name: 'MyTraining',
        component: () => import('@/views/training/MyTraining.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
      },
      {
        // One space per course, a tab per concern: content (no tab segment), settings, participants.
        // A tab is a param of the same route: switching tabs keeps the unsaved draft.
        path: 'elearning/builder/:courseId/:tab(settings|participants)?',
        name: 'ElearningCourseEdit',
        component: ElearningCourseEditorPage,
        meta: {
          requiresAuth: true,
          adminAccess: true,
          // The page stays mounted from one tab to another: it holds the draft being edited.
          viewKey: (route: RouteLocationNormalizedLoaded) => `elearning-course-${route.params.courseId}`,
        },
        beforeEnter: [guardElearningCourseWorkspace],
      },
      {
        path: 'elearning/builder',
        name: 'ElearningBuilder',
        redirect: { name: 'Elearning' },
      },
      {
        // The e-learning modules are listed in the training catalog, beside the trainings.
        path: 'elearning',
        name: 'Elearning',
        redirect: { name: 'TrainingList', query: { type: 'elearning' } },
      },
      {
        path: 'elearning/catalog',
        name: 'ElearningCatalog',
        redirect: { name: 'Elearning' },
      },
      {
        path: 'elearning/paths',
        name: 'LearningPathList',
        redirect: { name: 'TrainingSettings' },
      },
      {
        path: 'elearning/assignments',
        redirect: { name: 'Elearning' },
      },
      {
        path: 'elearning/stats',
        redirect: { name: 'TrainingStats' },
      },
      {
        path: 'elearning/assignment/:assignmentId',
        name: 'ElearningAssignmentPlayer',
        component: () => import('@/views/training/elearning/ElearningAssignmentPlayer.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
        beforeEnter: [guardElearningAssignmentPlayer],
      },
      {
        path: 'form/:formId/edit',
        name: 'TrainingFormEdit',
        component: () => import('@/views/training/form/Edit.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingFormEdit',
          editRouteName: 'TrainingFormEdit',
        },
        beforeEnter: [guardTrainingEvaluationFormsSettingsAccess],
      },
      {
        path: 'form/new',
        name: 'TrainingFormNew',
        component: () => import('@/views/training/form/Edit.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingFormNew',
          editRouteName: 'TrainingFormEdit',
        },
        beforeEnter: [guardTrainingEvaluationFormsSettingsAccess],
      },
      {
        path: 'budget',
        name: 'TrainingBudget',
        component: () => import('@/views/training/TrainingBudget.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingBudget',
        },
        beforeEnter: [guardTrainingBudget],
      },
      {
        path: ':trainingId',
        children: [
          {
            path: 'old',
            name: 'TrainingDetailsOld',
            component: () => import('@/views/training/TrainingDetailsOld.vue'),
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
          },
          {
            path: '',
            name: 'TrainingDetails',
            component: () => import('@/views/training/TrainingDetails.vue'),
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
          },
          {
            path: 'new',
            name: 'TrainingSessionNew',
            component: () => import('@/views/training/NewTrainingSession.vue'),
            beforeEnter: [guardOrganizeSession],
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
          },
          {
            path: 'edit',
            name: 'TrainingEdit',
            component: () => import('@/views/training/trainingNew/TrainingNew.vue'),
            beforeEnter: [guardTrainingEdit],
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
          }
        ]
      },
      {
        path: 'session/:trainingSessionId',
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
        children: [
          {
            path: '',
            name: 'TrainingSessionDetails',
            component: () => import('@/views/training/TrainingSessionDetails.vue'),
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
            beforeEnter: [guardTrainingEdit]
          },
          {
            path: 'evaluation/:resourceId', // not resource But object that container recruiterId
            name: 'TrainingSessionEvaluation',
            component: () => import('@/views/training/TrainingEvaluation.vue'),
            meta: {
              requiresAuth: true,
              navbarNotRequired: false,
            },
          },
        ],
      },
      {
        path: 'sessionDetails/:trainingSessionId',
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
        children: [
          {
            path: '',
            name: 'TrainingSessionDetail',
            component: () => import('@/views/training/TrainingSessionDetail.vue'),
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
            beforeEnter: [guardTrainingEdit]
          },
          {
            path: 'edit',
            name: 'TrainingSessionEdit',
            component: () => import('@/views/training/NewTrainingSession.vue'),
            meta: {
              requiresAuth: true,
              adminAccess: true,
            },
            beforeEnter: [guardTrainingEdit]
          },
        ],
      },
      {
        path: 'planning',
        name: 'TrainingPlanning',
        component: () => import('@/views/training/trainingPlanning/TrainingPlanning.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
        },
      },
      {
        // Next year's planning prepared from the sessions of a past year
        path: 'planning-draft',
        name: 'TrainingPlanDraft',
        component: () => import('@/views/training/trainingPlanDraft/TrainingPlanDraft.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingPlanDraft',
        },
        beforeEnter: [guardOrganizeSession],
      },
      {
        path: 'stats',
        name: 'TrainingStats',
        component: () => import('@/views/training/Stats.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingStats',
        },
        beforeEnter: [guardTrainingStats],
      },
      {
        path: 'wishes-attribution',
        name: 'TrainingWishesAttribution',
        component: () => import('@/views/training/TrainingWishesAttribution.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingWishesAttribution',
        },
        beforeEnter: [guardTrainingWishesAttribution],
      },
      {
        path: 'settings',
        name: 'TrainingSettings',
        component: () => import('@/views/training/Settings.vue'),
        meta: {
          requiresAuth: true,
          adminAccess: true,
          titleTranslationKey: 'pageTitle.trainingSettings',
        },
        beforeEnter: [guardTrainingSettingsAccess],
      },
    ]
  },





]

export default trainingRoutes.map(route => ({ ...route, meta: { ...route.meta, moduleName: MODULE_NAME, moduleId: MODULE_ID } }))
